import CallbackFactory, { CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import { Glue42Office } from "../../glue4office";
import { ShowGridMethodName, ValidateShowGridMethodName } from "./const";
import SheetFactory from "./sheet";
import { Excel, ShowGridParams, ValidateShowGridInvocationArgs } from "./types";
import { convertParams, parseAgmResult, getSheetId, sheetIdSeparator } from "./utils";

import { Glue42Core } from "@glue42/core";

import { version } from "../version";
import SheetImpl from "./sheet";
import ColumnConfig = Glue42Office.Excel.ColumnConfig;
import OpenSheetOptions = Glue42Office.Excel.OpenSheetOptions;

export default class ExcelImp implements Excel {
    public get all(): Glue42Office.Excel.Sheet[] {
        return Object.keys(this._sheets).map((k) => {
            return this._sheets[k].asSheetAPI;
        });
    }

    public get addinStatus(): boolean {
        return this._isExcelStarted;
    }

    private _registry: CallbackRegistry = CallbackFactory();
    private _agm: Glue42Core.AGM.API;
    private _sheets: { [key: string]: SheetFactory } = {};
    private _isExcelStarted: boolean = false;

    constructor(agm: Glue42Core.AGM.API) {
        this._agm = agm;
    }

    public init = (): Promise<void> => {
        return this.registerAgmMethod();
    }

    public openSheet = (sheetData: Glue42Office.Excel.OpenSheetConfig): Promise<Glue42Office.Excel.Sheet> => {
        return new Promise((resolve, reject) => {
            if (!sheetData) {
                reject("Can not open a sheet without config");
                return;
            }

            if (!sheetData.columnConfig || !Array.isArray(sheetData.columnConfig)) {
                reject("Missing or incorrect type `columnConfig` property");
                return;
            }

            if (!sheetData.data || !Array.isArray(sheetData.data)) {
                reject("Missing or incorrect type of `data` property");
                return;
            }
            const convertedParams = convertParams(sheetData.columnConfig, sheetData.data, sheetData.options);
            const successHandler = (r: any) => {
                if (parseAgmResult(r)) {
                    const { worksheet, workbook } = sheetData.options;
                    const id = convertedParams.params.cookie || getSheetId(workbook, worksheet);
                    const sheet = this.createSheet(id, sheetData, false);
                    resolve(sheet.asSheetAPI);
                }
            };

            const errorHandler = (err: any) => {
                reject(err);
            };
            return this.invokeShowGridMethod(convertedParams.params, convertedParams.agmOptions).then(successHandler).catch(errorHandler);
        });
    }

    public onAddinStatusChanged = (callback: (connected: boolean) => void): UnsubscribeFunction => {
        return this._registry.add("onAddinStatusChanged", callback);
    }

    public getWorkbooks = async (): Promise<Glue42Office.Excel.Book[]> => {
        const result = await this._agm.invoke("T42.ExcelScript.GetState");
        const workbooks = result.returned.state;

        const parsed = workbooks.map((book: any) => {
            const worksheets = book.worksheets.map(({ name }: { name: string }) => {
                const id = this.getCookieFromState(book) || getSheetId(book.name, name);
                let sheet = this._sheets[id];
                if (!sheet) {
                    sheet = this.createSheet(id, { columnConfig: [], options: { worksheet: name, workbook: book.name } }, false);
                }
                return sheet.asSheetAPI;
            });
            return { ...book, worksheets };
        });

        return parsed;
    }

    public getWorksheets = async (): Promise<Glue42Office.Excel.Sheet[]> => {
        const result = await this._agm.invoke("T42.ExcelScript.GetState");
        const workbooks = result.returned.state;

        const workSheets: Glue42Office.Excel.Sheet[] = [];
        workbooks.forEach((book: any) => {
            book.worksheets.forEach(({ name }: { name: string }) => {
                const id = this.getCookieFromState(book) || getSheetId(book.name, name);
                let sheet = this._sheets[id];
                if (!sheet) {
                    sheet = this.createSheet(id, { columnConfig: [], options: { worksheet: name, workbook: book.name } }, false);
                }
                workSheets.push(sheet.asSheetAPI);
            });
        });

        return workSheets;
    }

    public onNewWorksheet = (callback: (sheet: Glue42Office.Excel.Sheet) => void): UnsubscribeFunction => {
        return this._registry.add("onNewWorksheet", callback);
    }

    private getCookieFromState = (book: any) => {
        const keyName = "ExcelPad.cookie";

        const cookieState = book?.modulesState?.find((state: { key: string, value: any }) => state?.key === keyName);
        return cookieState?.value;
    }

    private createSheet = (id: string, sheetData: Glue42Office.Excel.OpenSheetConfig = { columnConfig: [] }, fireEvent = true): SheetImpl => {
        const sheet = new SheetFactory(id, sheetData, this.invokeShowGridMethod.bind(this));
        this._sheets[id] = sheet;

        if (fireEvent) {
            this._registry.execute("onNewWorksheet", sheet.asSheetAPI);
        }

        return sheet;
    }

    private createExcelFirstSheet = (id: string, args: ValidateShowGridInvocationArgs, fireEvent = true): SheetImpl => {
        const { worksheet, workbook } = args;
        // Excel First
        // this._workbooks[workbook][worksheet] = {};
        const columnConfig: ColumnConfig[] = JSON.parse(args.columnsAsJSON)
            .map((columnName: string) => ({ fieldName: columnName?.toLowerCase(), header: columnName }));

        const data = JSON.parse(args.dataAsJSON);
        // TODO: refactor
        const options: OpenSheetOptions = { worksheet, workbook, response: "image" };

        return this.createSheet(id, { columnConfig, data, options }, fireEvent);
    }

    private async registerAgmMethod(): Promise<void> {
        await this._agm.register({
            name: ValidateShowGridMethodName,
            accepts: "string cookie, string workbook, string worksheet, string updateType, string dataAsJSON",
        }, async (args: ValidateShowGridInvocationArgs, caller: Glue42Core.AGM.Instance) => {
            // tslint:disable-next-line:no-console
            console.log("args", args, this._sheets);
            const { cookie, workbook, worksheet } = args;
            if (workbook && worksheet) {
                const id = cookie || getSheetId(workbook, worksheet);
                let currentSheet = this._sheets[id];
                if (!currentSheet) {
                    currentSheet = this.createExcelFirstSheet(id, args);
                } else {
                    const asSheetAPI = currentSheet.asSheetAPI;
                    const wasPartiallyCreatedOnGet = !asSheetAPI.columnConfig || asSheetAPI.columnConfig.length === 0 || !asSheetAPI.workbook;

                    if (wasPartiallyCreatedOnGet) {
                        currentSheet = this.createExcelFirstSheet(id, args);
                    }
                }
                const result = await currentSheet._onUpdate(args);
                return result;
            }
            return undefined;
        });

        const methods = await this._agm.methods({ name: ShowGridMethodName });

        const waitForMethod = () => new Promise((resolve) => {
            this._agm.methodAdded((method) => {
                if (method.name === ShowGridMethodName) {
                    this._isExcelStarted = true;
                    this._registry.execute("onAddinStatusChanged", true);
                    resolve();
                }
            });
        });

        if (methods.length !== 0) {
            this._isExcelStarted = true;
            this._registry.execute("onAddinStatusChanged", true);
        } else {
            // not awaited because we want to resolve the factory function
            waitForMethod();
        }

        this._agm.methodRemoved((method) => {
            if (method.name === ShowGridMethodName) {
                waitForMethod();
                this._isExcelStarted = false;
                this._registry.execute("onAddinStatusChanged", false);
            }
        });
    }

    private invokeShowGridMethod(params: ShowGridParams, agmOptions: Glue42Core.AGM.InvokeOptions): Promise<object> {
        params.g4oVersion = version;
        return new Promise<object>((resolve, reject) => {
            if (this._isExcelStarted) {
                (agmOptions as any).method_response_timeout = agmOptions.methodResponseTimeoutMs;
                this._agm.invoke(ShowGridMethodName, params, null, agmOptions)
                    .then((response) => {
                        resolve(response);
                    }).catch((e) => {
                    reject(e);
                });
            } else {
                reject("Microsoft Excel with Tick42 Add-in is not running");
            }
        });
    }
}
